import { useMemo } from 'react';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import { Radio, Icon } from '@evertrue/et-components';
import { useEmailTemplatesProvider } from '../email-templates-context';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';

const PREVIEW_MAX_LENGTH = 200;

/**
 * Strip HTML tags and decode common entities to produce a short preview string.
 * Avoids DOMParser / innerHTML parsing — those force layout work that scales
 * poorly when many cards render at once. Output is rendered as a React text
 * node, so this is preview formatting, not sanitization.
 */
export const htmlToPlainText = (htmlString) => {
  if (typeof htmlString !== 'string' || !htmlString) return '';
  return htmlString
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, PREVIEW_MAX_LENGTH);
};

const EmailTemplateCard = ({ template, openTemplateForm, selectedTemplate }) => {
  const { deleteEmailTemplate, setSelectedTemplate } = useEmailTemplatesProvider();
  const { flagEnabled: signalEnabled } = useFeatureFlag('signal');
  const { flagEnabled: cadenceV2Enabled } = useFeatureFlag('cadenceV2Internal');
  const hasCadenceV2 = signalEnabled && cadenceV2Enabled;

  const { templateName, templateSubject, templateBody, id } = template;
  const isSelected = selectedTemplate?.id === id;
  const plainTextBody = useMemo(() => htmlToPlainText(templateBody), [templateBody]);

  const handleDelete = async (e) => {
    e.stopPropagation();

    const confirmed = await new Promise((resolve) => {
      EverTrue.Alert.confirm(
        {
          headline: 'Delete Template',
          content: `Are you sure you want to delete the template ${templateName}?`,
          buttonLabel: 'Delete',
        },
        (confirmation) => resolve(confirmation)
      );
    });

    if (confirmed) await deleteEmailTemplate(id);
  };

  const handleSelectTemplate = () => {
    if (!isSelected) {
      setSelectedTemplate(template);
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling

    // Save current scroll position before opening the form
    const scrollContainer = document.querySelector('.template-library--list');
    if (scrollContainer) {
      // Store scroll position in sessionStorage
      sessionStorage.setItem('templateListScrollPosition', scrollContainer.scrollTop);
    }

    setSelectedTemplate(template);
    openTemplateForm();
  };

  return (
    <div className={`email-template-card ${isSelected ? 'selected' : ''}`} onClick={handleSelectTemplate}>
      <Radio
        name="radio"
        label={templateName}
        onChange={handleSelectTemplate}
        showLabel={false}
        checked={isSelected}
        value={id}
      />
      <div>
        <div style={{ display: 'flex' }}>
          <div className="email--template-card--header">
            <p>
              <strong>{templateName}</strong>
            </p>
            <p>
              <strong>{templateSubject}</strong>
            </p>
          </div>
          <div className="email-template--icon-container">
            <Icon icon="edit" onClick={handleEditClick} isReachable={true} onKeyUp={() => {}} />
            <Icon icon="trash" onClick={handleDelete} isReachable={true} onKeyUp={() => {}} />
          </div>
        </div>
        {hasCadenceV2 && template.tags && template.tags.length > 0 && (
          <div className="email-template-card--tags">
            {template.tags.map((tag) => (
              <span key={tag} className="email-template-card--tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        <div style={{ marginBottom: 0 }} className="email-template-card-body">
          {plainTextBody}
        </div>
      </div>
    </div>
  );
};

EmailTemplateCard.propTypes = {
  template: PropTypes.object.isRequired,
  openTemplateForm: PropTypes.func.isRequired,
  selectedTemplate: PropTypes.object,
};

export default EmailTemplateCard;
